import LoadingBar from "@cloudscape-design/chat-components/loading-bar"
import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { useEffect, useState } from "react"
import type { DTADetails } from "../../../services/dtaApi"
import AnimatedDots from "./AnimatedDots"
import UploadSourceDocumentButton from "./UploadSourceDocumentButton"

interface EditorTabMessagesProps {
  /**
   * Async source-document extraction status for this DTA.
   */
  extractionStatus: DTADetails["extraction_status"]
  /**
   * Whether a recovered localStorage draft is loaded into the editor.
   */
  hasRecoveredDraft: boolean
  /**
   * Discards the recovered draft (clears it from localStorage).
   */
  onDiscardDraft: () => void
  /**
   * Reupload handler for the extraction-failed remedy. Undefined when reupload is unavailable
   * (non-DRAFT stage), in which case no reupload button is shown.
   */
  onReupload?: (file: File) => Promise<void>
}

/**
 * Editor-tab-scoped messages that belong with the DTA content rather than the page:
 *
 * - Recovered draft (action-required) — a scoped warning with a Discard action, at the top of the
 *   editor where the recovered content lives.
 * - Extraction in-progress (status) — an in-place gen-AI LoadingBar where the extracted content will
 *   appear, instead of a page-level banner. The editor below is locked while this runs.
 * - Extraction failed (error) — an in-content error state co-located with its remedy (reupload).
 *
 * Renders nothing when none apply, so a normal editor tab shows no messages.
 */
const EditorTabMessages = ({
  extractionStatus,
  hasRecoveredDraft,
  onDiscardDraft,
  onReupload,
}: EditorTabMessagesProps) => {
  // Let the user dismiss the extraction-failed error once they've acted on it (edited manually or
  // reuploaded). A fresh extraction attempt moves the status off FAILED, which re-arms the banner
  // so a subsequent failure shows again.
  const [extractionErrorDismissed, setExtractionErrorDismissed] = useState(false)
  useEffect(() => {
    if (extractionStatus !== "FAILED") setExtractionErrorDismissed(false)
  }, [extractionStatus])

  const showExtractionFailed = extractionStatus === "FAILED" && !extractionErrorDismissed

  if (!hasRecoveredDraft && extractionStatus !== "IN_PROGRESS" && !showExtractionFailed) {
    return null
  }

  return (
    <Box margin={{ bottom: "s" }}>
      <SpaceBetween size="s">
        {hasRecoveredDraft && (
          <Alert
            type="warning"
            action={<Button onClick={onDiscardDraft}>Discard</Button>}
            data-testid="recovered-draft-alert"
          >
            Recovered unsaved changes from a previous session.
          </Alert>
        )}
        {extractionStatus === "IN_PROGRESS" && (
          <Box data-testid="extraction-in-progress">
            <SpaceBetween size="xxs">
              <LoadingBar variant="gen-ai" />
              <Box color="text-body-secondary" fontSize="body-m">
                Extracting content from the source document
                <AnimatedDots /> This may take a minute.
              </Box>
            </SpaceBetween>
          </Box>
        )}
        {showExtractionFailed && (
          <Alert
            type="error"
            header="Content extraction failed"
            dismissible
            dismissAriaLabel="Dismiss content extraction error"
            onDismiss={() => setExtractionErrorDismissed(true)}
            action={onReupload && <UploadSourceDocumentButton onUpload={onReupload} />}
            data-testid="extraction-failed-alert"
          >
            You can edit the DTA manually or reupload the source document.
          </Alert>
        )}
      </SpaceBetween>
    </Box>
  )
}

export default EditorTabMessages
